package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.auth.registerCustomerAccount
import com.varabyte.kobweb.core.Page
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Page(routeOverride = "create-account")
@Composable
fun createAccountPage() {
    val coroutineScope = rememberCoroutineScope()
    var username by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var feedbackMessage by remember { mutableStateOf<String?>(null) }
    var isSubmitting by remember { mutableStateOf(false) }
    val passwordsDoNotMatch = confirmPassword.isNotEmpty() && password != confirmPassword

    CardPage(
        title = "Create Account",
        supportingText = "Create a customer account and sign in.",
    ) {
        Form(
            attrs = {
                onSubmit { event ->
                    event.preventDefault()

                    if (isSubmitting) {
                        return@onSubmit
                    }

                    if (passwordsDoNotMatch) {
                        feedbackMessage = "Passwords must match."
                        return@onSubmit
                    }

                    if (password.length < 12) {
                        feedbackMessage = "Use a password of at least 12 characters."
                        return@onSubmit
                    }

                    coroutineScope.launch {
                        isSubmitting = true
                        feedbackMessage = null
                        try {
                            val result =
                                registerCustomerAccount(
                                    username = username.trim(),
                                    password = password,
                                )

                            if (result.isSuccess) {
                                window.location.href = "/login?registered=true"
                            } else {
                                feedbackMessage = result.message
                            }
                        } catch (error: Throwable) {
                            if (error is CancellationException) {
                                throw error
                            }
                            feedbackMessage = error.message?.takeIf(String::isNotBlank) ?: "Unable to reach the server."
                        } finally {
                            isSubmitting = false
                        }
                    }
                }
                style {
                    property("display", "grid")
                    property("gap", "1rem")
                    property("margin-top", "1.5rem")
                }
            },
        ) {
            if (feedbackMessage != null) {
                P(
                    attrs = {
                        style {
                            property("margin", "0")
                            property("padding", "0.85rem 1rem")
                            property("border-radius", "0.75rem")
                            property("background", "#fee2e2")
                            property("color", "#b91c1c")
                        }
                    },
                ) {
                    Text(checkNotNull(feedbackMessage))
                }
            }

            Div {
                Label(
                    forId = "create-username",
                    attrs = {
                        style {
                            property("display", "block")
                            property("font-weight", "600")
                            property("margin-bottom", "0.45rem")
                        }
                    },
                ) {
                    Text("Username")
                }
                Input(
                    type = InputType.Text,
                    attrs = {
                        id("create-username")
                        value(username)
                        onInput { username = it.value }
                        attr("autocomplete", "username")
                        attr("required", "true")
                        style {
                            property("width", "100%")
                            property("padding", "0.8rem 0.95rem")
                            property("border", "1px solid #cbd5e1")
                            property("border-radius", "0.75rem")
                            property("font-size", "1rem")
                            property("box-sizing", "border-box")
                        }
                    },
                )
            }

            Div {
                Label(
                    forId = "create-password",
                    attrs = {
                        style {
                            property("display", "block")
                            property("font-weight", "600")
                            property("margin-bottom", "0.45rem")
                        }
                    },
                ) {
                    Text("Password")
                }
                Input(
                    type = InputType.Password,
                    attrs = {
                        id("create-password")
                        value(password)
                        onInput { password = it.value }
                        attr("autocomplete", "new-password")
                        attr("required", "true")
                        attr("minlength", "12")
                        style {
                            property("width", "100%")
                            property("padding", "0.8rem 0.95rem")
                            property("border", "1px solid #cbd5e1")
                            property("border-radius", "0.75rem")
                            property("font-size", "1rem")
                            property("box-sizing", "border-box")
                        }
                    },
                )
            }

            Div {
                Label(
                    forId = "create-confirm-password",
                    attrs = {
                        style {
                            property("display", "block")
                            property("font-weight", "600")
                            property("margin-bottom", "0.45rem")
                        }
                    },
                ) {
                    Text("Confirm password")
                }
                Input(
                    type = InputType.Password,
                    attrs = {
                        id("create-confirm-password")
                        value(confirmPassword)
                        onInput {
                            confirmPassword = it.value
                            if (feedbackMessage == "Passwords must match.") {
                                feedbackMessage = null
                            }
                        }
                        attr("autocomplete", "new-password")
                        attr("required", "true")
                        style {
                            property("width", "100%")
                            property("padding", "0.8rem 0.95rem")
                            property("border", "1px solid #cbd5e1")
                            property("border-radius", "0.75rem")
                            property("font-size", "1rem")
                            property("box-sizing", "border-box")
                        }
                    },
                )
                if (passwordsDoNotMatch) {
                    P(
                        attrs = {
                            style {
                                property("margin", "0.6rem 0 0")
                                property("color", "#b91c1c")
                                property("font-size", "0.95rem")
                            }
                        },
                    ) {
                        Text("Passwords must match.")
                    }
                }
            }

            Button(
                attrs = {
                    attr("type", "submit")
                    if (passwordsDoNotMatch) {
                        attr("disabled", "true")
                    }
                    style {
                        property("padding", "0.9rem 1rem")
                        property("border", "0")
                        property("border-radius", "0.8rem")
                        property("background", if (passwordsDoNotMatch) "#94a3b8" else "#2563eb")
                        property("color", "#ffffff")
                        property("font-size", "1rem")
                        property("font-weight", "700")
                        property("cursor", if (passwordsDoNotMatch) "not-allowed" else "pointer")
                    }
                },
            ) {
                Text(if (isSubmitting) "Creating..." else "Create account")
            }
        }
    }
}
